import { StrawRow } from '../components/embryo-table/EmbryoDetailsTable';
import { getExistingEmbryoSpecimens } from '../api/specimensApi';
import {
  AccountStrawState,
  AddStrawLevelDataLocation,
  ExistingEmbryoSpecimen,
  ExistingEmbryoStrawDataPayload,
} from '../types/interfaces/addStrawLevelData.interfaces';
import { SpecimenLocation } from '../types/interfaces/specimen.interfaces';
import { StorageLocation } from '../types/interfaces/inventoryTransaction.interfaces';
import { hashCode } from '../utils/commonUtils';

const mapSpecimenLocationToAddStrawLocation = (location: SpecimenLocation): AddStrawLevelDataLocation => ({
  storageCanisterId: location.storageCanisterId,
  siteId: location.storageCanister?.storageTank?.storageSite?.storageSiteId ?? 0,
  tankId: location.storageCanister?.storageTank?.storageTankId ?? 0,
  siteName: location.storageCanister?.storageTank?.storageSite?.name ?? '-',
  tankName: location.storageCanister?.storageTank?.name ?? '-',
  canisterName: location.storageCanister?.name ?? '-',
  quantity: location.quantity,
});

const toStorageLocation = (location: AddStrawLevelDataLocation): StorageLocation => ({
  site: { name: location.siteName, value: String(location.siteId) },
  tank: { name: location.tankName, value: String(location.tankId) },
  canister: { name: location.canisterName, value: String(location.storageCanisterId) },
  quantity: { value: location.quantity },
  strawCodesText: '',
  id: hashCode(`${location.storageCanisterId}-${location.siteId}`),
});

const mapExistingEmbryoSpecimenToAccountStrawState = (existingSpecimen: ExistingEmbryoSpecimen): AccountStrawState => ({
  accountId: existingSpecimen.account.accountId ?? 0,
  accountName: existingSpecimen.account.name,
  specimenId: existingSpecimen.specimenId,
  currentQuantity: existingSpecimen.quantityAvailable,
  numStraws: 0,
  strawData: [],
  storageLocations: (existingSpecimen.specimenLocations ?? []).map(location =>
    toStorageLocation(mapSpecimenLocationToAddStrawLocation(location)),
  ),
});

export const mapExistingEmbryoSpecimensToAccountStates = (existingSpecimens: ExistingEmbryoSpecimen[]): AccountStrawState[] =>
  existingSpecimens.map(mapExistingEmbryoSpecimenToAccountStrawState);

export const getUniqueStorageLocationsFromAccountStates = (accountStates: AccountStrawState[]): StorageLocation[] => {
  const uniqueLocations = new Map<number, StorageLocation>();

  accountStates.forEach(accountState => {
    accountState.storageLocations.forEach(location => {
      const canisterId = Number(location.canister.value);
      if (!canisterId || uniqueLocations.has(canisterId)) return;

      uniqueLocations.set(canisterId, {
        ...location,
        strawCodesText: '',
      });
    });
  });

  return Array.from(uniqueLocations.values());
};

export const createEmptyStraw = (index: number): StrawRow => ({
  embryoStrawNumber: String(index + 1),
  embryoGrade: '',
  embryoStage: '',
  embryoCountPerStraw: 0,
});

export const buildStrawDataForCount = (count: number, existing: StrawRow[] = []): StrawRow[] => {
  if (count <= 0) return [];

  let updatedStrawData = [...existing];

  if (count > existing.length) {
    for (let index = existing.length; index < count; index++) {
      updatedStrawData.push(createEmptyStraw(index));
    }
  } else if (count < existing.length) {
    updatedStrawData = updatedStrawData.slice(0, count);
  }

  // Keep existing values (including locked custom straw numbers); resequence assigns unlocked ones.
  return updatedStrawData;
};

export const resequenceAccountStrawStates = (accountStates: AccountStrawState[]): AccountStrawState[] => {
  let counter = 0;

  return accountStates.map(accountState => {
    const resequencedStrawData = accountState.strawData.map(strawRow => {
      // Preserve user-entered custom straw numbers (e.g. "test") when adding/removing rows.
      if (strawRow.lockedCode) {
        const numeric = Number(String(strawRow.embryoStrawNumber).replace(/^0+/, ''));
        if (!Number.isNaN(numeric) && numeric > 0) {
          counter = Math.max(counter, numeric);
        } else {
          counter += 1;
        }
        return strawRow;
      }

      counter += 1;
      return {
        ...strawRow,
        embryoStrawNumber: String(counter),
      };
    });

    return {
      ...accountState,
      numStraws: accountState.strawData.length,
      strawData: resequencedStrawData,
    };
  });
};

const getMatchedCanisterId = (storageLocations: StorageLocation[], strawNumber: string): number | undefined => {
  const matchedLocation = storageLocations.find(storageLocation => {
    const locationStrawCodes = (storageLocation.strawCodesText ?? '')
      .split(',')
      .map(strawCode => strawCode.trim())
      .filter(Boolean);

    return locationStrawCodes.includes(strawNumber);
  });

  return matchedLocation ? Number(matchedLocation.canister.value) : undefined;
};

export const buildExistingEmbryoStrawSavePayload = (
  accountStates: AccountStrawState[],
  storageLocations: StorageLocation[],
): ExistingEmbryoStrawDataPayload[] => {
  if (accountStates.length === 0) return [];

  const specimenId = accountStates[0].specimenId;

  return [
    {
      specimenId,
      embryoStraws: accountStates.flatMap(accountState =>
        accountState.strawData.map(strawRow => ({
          embryoStrawNumber: strawRow.embryoStrawNumber,
          embryoGrade: strawRow.embryoGrade ?? '',
          embryoStage: strawRow.embryoStage,
          embryoCountPerStraw: Number(strawRow.embryoCountPerStraw) || 0,
          accountId: accountState.accountId,
          storageCanisterId: getMatchedCanisterId(storageLocations, strawRow.embryoStrawNumber) ?? 0,
        })),
      ),
    },
  ];
};

export const loadAccountStrawStatesForSpecimen = async (specimenId: number) => {
  const { data: existingSpecimens } = await getExistingEmbryoSpecimens(specimenId);
  const accountStates = mapExistingEmbryoSpecimensToAccountStates(existingSpecimens).filter(
    accountState => accountState.currentQuantity > 0,
  );

  return {
    accountStates,
    storageLocations: getUniqueStorageLocationsFromAccountStates(accountStates),
  };
};
